import * as ToneJS from 'tone';
import Soundfont, { InstrumentName } from 'soundfont-player';
import {
    Note,
    NoteVelocity
} from './core';

import { AudioSink } from '../audio/routing';
import { assert } from '../utils/error';
import { TrackOutputType } from './tracks';
import { ThreeVoiceSynth } from '../audio/instruments/ThreeMonoSynth';
import TR808Drum from '../audio/instruments/TR808Drum';


const INSTRUMENT_NAMES = [
    "acoustic_grand_piano",
    "bright_acoustic_piano",
    "electric_grand_piano",
    "honkytonk_piano",
    "electric_piano_1",
    "electric_piano_2",
    "harpsichord",
    "clavinet",
    "celesta",
    "glockenspiel",
    "music_box",
    "vibraphone",
    "marimba",
    "xylophone",
    // "tubular_bells",
    "dulcimer",
    "drawbar_organ",
    "percussive_organ",
    "rock_organ",
    "church_organ",
    "reed_organ",
    "accordion",
    "harmonica",
    "tango_accordion",
    "acoustic_guitar_nylon",
    "acoustic_guitar_steel",
    "electric_guitar_jazz",
    "electric_guitar_clean",
    "electric_guitar_muted",
    "overdriven_guitar",
    "distortion_guitar",
    "guitar_harmonics",
    "acoustic_bass",
    "electric_bass_finger",
    "electric_bass_pick",
    "fretless_bass",
    "slap_bass_1",
    "slap_bass_2",
    "synth_bass_1",
    "synth_bass_2",
    "violin",
    "viola",
    "cello",
    "contrabass",
    "tremolo_strings",
    "pizzicato_strings",
    "orchestral_harp",
    "timpani",
    "string_ensemble_1",
    "string_ensemble_2",
    "synth_strings_1",
    "synth_strings_2",
    "choir_aahs",
    "voice_oohs",
    "synth_choir",
    "orchestra_hit",
    "trumpet",
    "trombone",
    "tuba",
    "muted_trumpet",
    "french_horn",
    "brass_section",
    "synth_brass_1",
    "synth_brass_2",
    "soprano_sax",
    "alto_sax",
    "tenor_sax",
    "baritone_sax",
    "oboe",
    "english_horn",
    "bassoon",
    "clarinet",
    "piccolo",
    "flute",
    "recorder",
    "pan_flute",
    "blown_bottle",
    "shakuhachi",
    "whistle",
    "ocarina",
    "lead_1_square",
    "lead_2_sawtooth",
    "lead_3_calliope",
    "lead_4_chiff",
    "lead_5_charang",
    "lead_6_voice",
    "lead_7_fifths",
    "lead_8_bass__lead",
    "pad_1_new_age",
    "pad_2_warm",
    "pad_3_polysynth",
    "pad_4_choir",
    "pad_5_bowed",
    "pad_6_metallic",
    "pad_7_halo",
    "pad_8_sweep",
    "fx_1_rain",
    "fx_2_soundtrack",
    "fx_3_crystal",
    "fx_4_atmosphere",
    "fx_5_brightness",
    "fx_6_goblins",
    "fx_7_echoes",
    "fx_8_scifi",
    "sitar",
    "banjo",
    "shamisen",
    "koto",
    "kalimba",
    "bagpipe",
    "fiddle",
    "shanai",
    "tinkle_bell",
    "agogo",
    "steel_drums",
    "woodblock",
    "taiko_drum",
    "melodic_tom",
    "synth_drum",
    "reverse_cymbal",
    "guitar_fret_noise",
    "breath_noise",
    "seashore",
    "bird_tweet",
    "telephone_ring",
    "helicopter",
    "applause",
    "gunshot"
];

export function listSoundfondInstruments(): RydInstrumentDescription[] {
    return INSTRUMENT_NAMES.map(name => ({
        id: name,
        name: name.replace(/_/g, ' ')
    }));
}

export interface RydInstrumentDescription {
    id: string;
    name: string;
}

export type InstrumentOutputTypeAware = {
    getOutputType: () => TrackOutputType;
    getOutputNode: () => ToneJS.OutputNode | undefined;
}

export interface RydInstrument extends RydInstrumentDescription, InstrumentOutputTypeAware {
    triggerInSequence: (notes: Note[], duration: string, velocity: NoteVelocity, time) => void;
    noteOn: (note: Note, velocity: NoteVelocity) => void;
    noteOff: (note: Note) => void;
};

/**
 * Internal interface that extends RydInstrument with additional properties for internal use.
 * Expected is that this is the interface of all sound sources whose parameters are controllable by the mixer.
 *
 * External instruments (e.g., MIDI out) are jsut RydInstruments.
 */
export interface RydInternalInstrument extends RydInstrument {
    outputNode: ToneJS.OutputNode;
}

export class RydVirtualInstrument implements RydInternalInstrument {
    id: string;
    name: string;
    description?: string;
    readonly sequenceSynth: ToneJS.PolySynth;
    outputNode: ToneJS.OutputNode;

    constructor(
        private audioContext: AudioContext,
        id: string,
        name: string,
        description?: string,
    ) {
        this.id = id;
        this.name = name;
        this.description = description;

        // FIXME: make this parameterizable
        this.sequenceSynth = new ToneJS.PolySynth();
        this.outputNode = this.sequenceSynth.output;
    }

    getInstrumentDescription() {
        return {
            id: this.id,
            name: this.name
        };
    }

    triggerInSequence = (notes: Note[], duration: string, velocity: NoteVelocity, time) => {
        this.sequenceSynth.triggerAttackRelease(notes.map(n => n.getName()), duration, time, velocity / 127);
    }

    noteOn = (note: Note, velocity: NoteVelocity) => {
        this.sequenceSynth.triggerAttack(note.getName(), ToneJS.now(), velocity / 127);
    }
    noteOff = (note: Note) => {
        this.sequenceSynth.triggerRelease(note.getName());
    }
    getOutputType = () => {
        return 'VSYNTH' as TrackOutputType;
    }
    getOutputNode = () => {
        return this.sequenceSynth.output;
    }
}

export class RydSFInstrument implements RydInternalInstrument {
    id: string;
    name: string;
    soundfontUrl: string;
    instrumentName: InstrumentName;
    player: Soundfont.Player;
    private currentlyActiveNotes: (Soundfont.Player | undefined)[] = Array(Note.all.length).fill(undefined);

    private audioContext: AudioContext;
    outputNode: GainNode;

    constructor(
        id: string,
        name: string,
        instrumentName: InstrumentName,
        audioContext?: AudioContext
    ) {
        assert(audioContext, 'AudioContext must be provided for RydSFInstrument');
        if (!audioContext) return;

        this.id = id;
        this.name = name;
        // this.soundfontUrl = soundfontUrl;
        this.instrumentName = instrumentName;
        if (audioContext) {
            this.audioContext = audioContext;
            this.outputNode = audioContext.createGain();
        }

        // Load the "acoustic_grand_piano" soundfont. You can change to any available instrument.

        // https://gleitz.github.io/midi-js-soundfonts/MusyngKite/names.json
        //'acoustic_grand_piano'
        Soundfont.instrument(this.audioContext, instrumentName, { destination: this.outputNode })
            .then((inst) => this.player = inst)
            .catch((err) => console.error('Error loading instrument:', err));
    }

    getInstrumentDescription() {
        return {
            id: this.id,
            name: this.name
        };
    }

    triggerInSequence = (notes: Note[], duration, velocity: NoteVelocity, time) => {
        if (this.player) {
            notes.forEach(note => {
                const ds = ToneJS.Time(duration).toSeconds();
                if (!this.player) return;
                this.player.play(note.getName(), this.audioContext.currentTime, { duration: ds, gain: velocity / 127 });
            });
        }
    }
    noteOn = (note: Note, velocity: NoteVelocity) => {
        if (this.player) {
            const stop = this.player.start(note.getName(), this.audioContext.currentTime, { gain: velocity / 127 });
            this.currentlyActiveNotes[note.index] = stop;
        }
    }
    noteOff = (note: Note) => {
        if (this.player && this.currentlyActiveNotes[note.index]) {
            this.currentlyActiveNotes[note.index]?.stop(this.audioContext.currentTime);
            this.currentlyActiveNotes[note.index] = undefined;
        }
    }
    getOutputType = () => {
        return 'SF2' as TrackOutputType;
    }

    getOutputNode = () => {
        return this.outputNode;
    }
}

export class RydExternalMidiInstrument implements RydInstrument {
    id: string;
    name: string;
    midiSink: AudioSink;
    midiOutput?: any;

    constructor(
        id: string,
        name: string,
        midiSink: AudioSink,
    ) {
        this.id = id;
        this.name = name;
        this.midiSink = midiSink;
    }

    getInstrumentDescription() {
        return {
            id: this.id,
            name: this.name
        };
    }

    triggerInSequence = (notes, duration, time) => {
        notes.forEach(note => {
            console.warn('trigger in sequence ext synth using bogus velocity')
            this.noteOn(note, 100);
            // FIXME: use AudioContext to schedule the note off event
            setTimeout(() => {
                this.noteOff(note);
            }, ToneJS.Time(duration).toMilliseconds());
        });
    }

    noteOn = (note: Note, velocity: NoteVelocity) => this.midiOutput?.send(new Uint8Array([0x90, note.index, velocity]));
    noteOff = (note: Note) => this.midiOutput?.send(new Uint8Array([0x80, note.index, 0x00]));

    getOutputType = () => {
        return 'EXTMIDI' as TrackOutputType;
    }

    getOutputNode = () => {
        return undefined;
    }
}

export type RydInstrumentFactory = {
    id: string;
    name: string;
    create: (audioContext: AudioContext) => RydInternalInstrument;
}


export function listVirtualInstruments(): RydInstrumentFactory[] {
    return [{
        id: 'ThreeMonoSynth',
        name: '3VS',
        create: (ac: AudioContext) => {
            const synth = ThreeVoiceSynth.getFactory()(ac);

            return synth;
        }
    }, {
        id: 'SimplePolySynth',
        name: 'Simple Poly Synth',
        create: (ac: AudioContext) => {
            const synth = new RydVirtualInstrument(ac, 'SimplePolySynth', 'Simple Poly Synth');
            return synth;
        }
    },
    {
        id: 'TrAchtDrum',
        name: 'TrAcht',
        create: (ac: AudioContext) => {
            const drum = new TR808Drum();
            return drum;
        }
    }];
}

export type RydInstrumentAssignment = {
    instrument: RydInstrument;
    trackId: number;
}

